import { DocumentIcon, QuestionMarkCircleIcon, InformationCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Modal from "../common/Modal";
import React, { useState, useRef } from "react";
import { useStorage } from "../../hooks/useStorage";
import { serverTimestamp, Timestamp } from "firebase/firestore";
import { useTemplates } from "../../hooks/useTemplates";
import { Template, TemplateType } from "../../types/template";
import { useLanguage } from "../../contexts/LanguageContext";
import { customerKeys } from "../customers/customerKeys";
import { quotationKeys } from "../quotations/quotationKeys";
import toast from "react-hot-toast";
import { useOrganizations } from "../../hooks/useOrganizations";

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const templateTypes = [
  { id: 'contract', name: 'templates.createTemplateModal.contract' },
  { id: 'quotation', name: 'templates.createTemplateModal.quotation' }
];

const CreateTemplateModal = ({ isOpen, onClose }: CreateTemplateModalProps) => {
  const { t } = useLanguage();
  const docFileInputRef = useRef<HTMLInputElement>(null);
  const [selectedDocFile, setSelectedDocFile] = useState<File | null>(null);
  const [templateName, setTemplateName] = useState<string>('');
  const [templateDesc, setTemplateDesc] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('contract');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { addTemplate } = useTemplates();
  const { organizations, loading: loadingOrganizations } = useOrganizations();

  const { uploadFile } = useStorage();

  const handleDocFileSelect = () => {
    docFileInputRef.current?.click();
  };

  const handleDocFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        setErrorMessage(t("templates.createTemplateModal.errors.invalidFormat"));
        setSelectedDocFile(null);
        return;
      }
      setSelectedDocFile(file);
      setUploadSuccess(false);
      setErrorMessage(null);
    }
    // Töröljük a file input értékét, hogy ugyanazt a fájlt újra ki lehessen választani
    if (e.target.value) e.target.value = '';
  };

  const handleTypeChange = (typeId: string) => {
    setSelectedType(typeId);
  };

  const isFormValid = () => {
    return selectedDocFile && templateName.trim() !== '' && selectedOrganizationId !== '';
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setIsUploading(true);
    setErrorMessage(null);

    try {
      // Fájl feltöltése Firebase Storage-ba
      const fileName = `templates/${selectedType}/${Date.now()}-${selectedDocFile!.name}`;
      const fileUrl = await uploadFile(selectedDocFile!, fileName);

      // Template adatok mentése Firestore-ba
      const newTemplate: Omit<Template, 'id'> = {
        name: templateName,
        description: templateDesc,
        type: selectedType as TemplateType,
        organizationId: selectedOrganizationId,
        fileUrl: fileUrl,
        fileName: fileName,
        createdAt: serverTimestamp() as unknown as Timestamp,
        updatedAt: serverTimestamp() as unknown as Timestamp,
      };

      // Firestore-ba mentés
      await addTemplate(newTemplate as Template);

      setUploadSuccess(true);
      toast.success(t("templates.messages.created"));

      // Késleltetett bezárás, hogy a felhasználó lássa a sikeres feltöltés visszajelzést
      setTimeout(() => {
        // Form reset
        setSelectedDocFile(null);
        setTemplateName('');
        setTemplateDesc('');
        setSelectedOrganizationId('');
        setUploadSuccess(false);
        onClose();
      }, 500);
    } catch (error) {
      console.error("Hiba a fájl feltöltése során:", error);
      setErrorMessage(t("templates.messages.error"));
      toast.error(t("templates.messages.error"));
      setUploadSuccess(false);
    } finally {
      setIsUploading(false);
    }
  };

  const getAvailableKeys = () => {
    switch (selectedType) {
      case 'contract':
        return Object.keys(customerKeys);
      case 'quotation':
        return Object.keys(quotationKeys);
      default:
        return [];
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("templates.createTemplateModal.title")}
      size="6xl"
    >
      <div className="p-6 sm:p-6 bg-white">
        <p className="text-gray-700 text-sm sm:text-base mb-5 sm:mb-6">
          {t("templates.createTemplateModal.description")}
        </p>

        <div className="grid gap-5 sm:gap-6 mb-6 sm:mb-6">
          <div>
            <label className="block text-gray-700 text-sm sm:text-sm font-medium mb-2 sm:mb-2">{t("templates.createTemplateModal.name")}</label>
            <input
              type="text"
              className="w-full bg-white border border-gray-300 rounded-md py-2.5 sm:py-2.5 px-3 sm:px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
              placeholder={t("templates.createTemplateModal.namePlaceholder")}
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm sm:text-sm font-medium mb-2 sm:mb-2">{t("templates.createTemplateModal.description")}</label>
            <textarea
              className="w-full bg-white border border-gray-300 rounded-md py-2.5 sm:py-2.5 px-3 sm:px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
              placeholder={t("templates.createTemplateModal.descriptionPlaceholder")}
              rows={3}
              value={templateDesc}
              onChange={(e) => setTemplateDesc(e.target.value)}
            ></textarea>
          </div>

          <div>
            <label className="block text-gray-700 text-sm sm:text-sm font-medium mb-2 sm:mb-2">
              {t("templates.createTemplateModal.organization")}
            </label>
            <select
              className="w-full bg-white border border-gray-300 rounded-md py-2.5 sm:py-2.5 px-3 sm:px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm appearance-none"
              value={selectedOrganizationId}
              onChange={(e) => setSelectedOrganizationId(e.target.value)}
              required
            >
              <option value="">{t("templates.createTemplateModal.selectOrganization")}</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2 sm:mb-2">
              <label className="block text-gray-700 text-sm sm:text-sm font-medium">{t("templates.createTemplateModal.documentType")}</label>
              <div className="group relative">
                <InformationCircleIcon className="h-5 w-5 text-primary-600 hover:text-primary-700 cursor-help transition-colors" />
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-white text-gray-800 text-xs rounded-lg py-3 px-4 shadow-lg border border-gray-200 h-[400px] overflow-y-auto min-w-[280px] sm:min-w-[300px] max-w-[90vw] sm:max-w-[400px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-500/50">
                    <div className="font-medium text-sm mb-2">{t("templates.createTemplateModal.availableFields")}</div>
                    <p className="text-gray-600 mb-3 leading-relaxed text-sm">
                      {t("templates.createTemplateModal.fieldsDescription", {
                        type: selectedType === 'contract' ? 'ügyfél' : 'ajánlat',
                        format: '{{xyz}}'
                      })}
                    </p>
                    <div className="space-y-2 bg-gray-100 rounded-md p-3 mb-2">
                      {getAvailableKeys().map((key, index) => (
                        <div key={index} className="flex items-center gap-2 text-gray-700 font-mono text-xs hover:bg-gray-200 rounded px-2 py-1 transition-colors">
                          <span className="text-primary-600">{`{{`}</span>
                          <span className="break-all">{key}</span>
                          <span className="text-primary-600">{`}}`}</span>
                        </div>
                      ))}
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 sm:gap-4 bg-gray-50 p-3 rounded-md border border-gray-200">
              {templateTypes.map(type => (
                <label key={type.id} className="flex items-center cursor-pointer mb-1">
                  <input
                    type="radio"
                    name="templateType"
                    value={type.id}
                    checked={selectedType === type.id}
                    onChange={() => handleTypeChange(type.id)}
                    className="sr-only"
                  />
                  <div className={`w-4 sm:w-4 h-4 sm:h-4 rounded-full border flex items-center justify-center mr-2 sm:mr-2 ${selectedType === type.id
                    ? 'border-primary-600 bg-primary-500/20'
                    : 'border-gray-400'
                    }`}>
                    {selectedType === type.id && <div className="w-2 sm:w-2 h-2 sm:h-2 rounded-full bg-primary-600"></div>}
                  </div>
                  <span className="text-gray-800 text-sm sm:text-sm">{t(type.name)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-5 sm:pt-5 mb-6 sm:mb-6">
          <h3 className="text-sm sm:text-base font-medium text-gray-800 mb-3 sm:mb-3">{t("templates.createTemplateModal.uploadTemplate")}</h3>
          <p className="text-sm sm:text-sm text-gray-600 mb-4 sm:mb-4">
            {t("templates.createTemplateModal.uploadTemplateDescription")}
          </p>

          <div
            onClick={handleDocFileSelect}
            className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center cursor-pointer transition-colors ${selectedDocFile
              ? 'border-primary-600 bg-primary-500/10'
              : 'border-gray-300 hover:border-primary-500/50 hover:bg-gray-50'
              }`}
          >
            <input
              type="file"
              ref={docFileInputRef}
              className="hidden"
              accept=".docx"
              onChange={handleDocFileChange}
            />
            {selectedDocFile ? (
              <div className="flex flex-col items-center">
                <DocumentIcon className="h-12 w-12 sm:h-14 sm:w-14 text-primary-600 mb-2 sm:mb-3" />
                <p className="text-primary-600 text-sm sm:text-base font-medium mb-1 sm:mb-1">{selectedDocFile.name}</p>
                <p className="text-sm text-gray-600">
                  {Math.round(selectedDocFile.size / 1024)} KB
                </p>
              </div>
            ) : (
              <>
                <DocumentIcon className="h-12 w-12 sm:h-14 sm:w-14 mx-auto text-gray-400 mb-3 sm:mb-4" />
                <p className="text-gray-700 text-sm sm:text-base font-medium mb-1 sm:mb-1">{t("templates.createTemplateModal.uploadTemplateDescription")}</p>
                <p className="text-sm text-gray-500">{t("templates.createTemplateModal.uploadTemplateDescription")}</p>
                <p className="text-sm text-primary-600 mt-3 font-medium">
                  {t("templates.createTemplateModal.supportedFormat")}
                </p>
              </>
            )}
          </div>
        </div>

        {errorMessage && (
          <div className="mb-5 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-300 rounded-md text-red-600 text-sm sm:text-sm">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mr-2" />
              <span>{errorMessage}</span>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6 sm:mt-6 pt-4 border-t border-gray-200 space-x-3 sm:space-x-4">
          <button
            onClick={onClose}
            className="px-4 sm:px-5 py-2 sm:py-2.5 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-md transition-colors text-sm sm:text-sm font-medium shadow-sm"
            disabled={isUploading}
          >
            {t("templates.createTemplateModal.cancel")}
          </button>
          <button
            onClick={handleSubmit}
            className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-md transition-colors flex items-center text-sm sm:text-sm font-medium shadow-sm ${isFormValid() && !isUploading
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : isUploading
                ? 'bg-primary-600 text-white cursor-wait'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            disabled={!isFormValid() || isUploading}
          >
            {isUploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t("templates.createTemplateModal.uploading")}...
              </>
            ) : uploadSuccess ? (
              <>
                <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-2 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {t("templates.createTemplateModal.uploadSuccess")}
              </>
            ) : (
              t("templates.createTemplateModal.createTemplate")
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateTemplateModal;

